/**
 * Logger utility to standardize and control console logging throughout the application
 * Helps prevent excessive console logs in production and development
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

type LoggerOptions = {
  minLevel: LogLevel;
  allowDuplicates: boolean;
  maxLogsPerMinute: number;
  enabled: boolean;
  logTimestamps: boolean;
  namespace?: string;
};

/**
 * Default options with environment-aware configuration priorities:
 * 1. Environment variables
 * 2. Production vs development detection
 * 3. Sensible defaults
 */
const defaultOptions: LoggerOptions = {
  minLevel: 
    process.env.REACT_APP_LOG_LEVEL 
      ? parseInt(process.env.REACT_APP_LOG_LEVEL, 10) as LogLevel 
      : (process.env.NODE_ENV === 'production' ? LogLevel.WARN : LogLevel.DEBUG),
  
  allowDuplicates: process.env.REACT_APP_ALLOW_DUPLICATE_LOGS === 'true',
  
  maxLogsPerMinute: 
    process.env.REACT_APP_MAX_LOGS_PER_MINUTE 
      ? parseInt(process.env.REACT_APP_MAX_LOGS_PER_MINUTE, 10) 
      : 10,
  
  enabled: process.env.REACT_APP_DISABLE_LOGS !== 'true',
  
  logTimestamps: process.env.REACT_APP_LOG_TIMESTAMPS !== 'false',
};

class Logger {
  private options: LoggerOptions;
  private recentLogs: Map<string, { timestamp: number; count: number }> = new Map();
  private logCounter = 0;
  private lastResetTime: number = Date.now();
  
  constructor(options: Partial<LoggerOptions> = {}) {
    this.options = { ...defaultOptions, ...options };
  }
  
  /**
   * Log a debug message (lowest priority)
   */
  debug(message: string, ...args: unknown[]): void {
    this.log(LogLevel.DEBUG, message, ...args);
  }
  
  /**
   * Log an info message
   */
  info(message: string, ...args: unknown[]): void {
    this.log(LogLevel.INFO, message, ...args);
  }
  
  /**
   * Log a warning message
   */
  warn(message: string, ...args: unknown[]): void {
    this.log(LogLevel.WARN, message, ...args);
  }
  
  /**
   * Log an error message (highest priority)
   */
  error(message: string, ...args: unknown[]): void {
    this.log(LogLevel.ERROR, message, ...args);
  }
  
  /**
   * Table logging (convenience wrapper)
   */
  table(data: unknown, columns?: string[]): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    console.table(data, columns);
  }
  
  /**
   * Create a namespace-specific logger instance
   */
  getNamespacedLogger(namespace: string): Logger {
    return new Logger({
      ...this.options,
      namespace,
    });
  }
  
  /**
   * Update logger options
   */
  setOptions(options: Partial<LoggerOptions>): void {
    this.options = { ...this.options, ...options };
  }
  
  /**
   * Determines if a log should be output based on level,
   * enabled status, and rate limiting
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.options.enabled) return false;
    if (level < this.options.minLevel) return false;
    
    const now = Date.now();
    if (now - this.lastResetTime > 60000) {
      this.logCounter = 0;
      this.lastResetTime = now;
    }
    
    if (this.logCounter >= this.options.maxLogsPerMinute && level < LogLevel.ERROR) {
      return false; // Rate limit exceeded, but always let ERROR logs through
    }
    
    this.logCounter++;
    return true;
  }
  
  /**
   * Prevents duplicate logs within a short time window
   * Returns true if the message is a duplicate that should be skipped
   */
  private isDuplicate(message: string): boolean {
    if (this.options.allowDuplicates) return false;
    
    const logKey = message.slice(0, 100); // Truncate to avoid memory issues
    const now = Date.now();
    const recentLog = this.recentLogs.get(logKey);
    
    if (recentLog && now - recentLog.timestamp < 1000) {
      this.recentLogs.set(logKey, { 
        timestamp: now, 
        count: recentLog.count + 1 
      });
      return true;
    }
    
    // Clean up entries older than 1 minute to control memory usage
    if (this.recentLogs.size > 100) {
      const oldestTime = now - 60000;
      Array.from(this.recentLogs.entries()).forEach(([key, log]) => {
        if (log.timestamp < oldestTime) {
          this.recentLogs.delete(key);
        }
      });
    }
    
    this.recentLogs.set(logKey, { timestamp: now, count: 1 });
    return false;
  }
  
  /**
   * Core logging function that handles formatting and console output
   */
  private log(level: LogLevel, message: string, ...args: unknown[]): void {
    if (!this.shouldLog(level)) return;
    if (this.isDuplicate(message)) return;
    
    const formattedMessage = this.formatMessage(level, message);
    
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage, ...args);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, ...args);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, ...args);
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage, ...args);
        break;
    }
  }
  
  /**
   * Format the log message with timestamp and namespace if configured
   */
  private formatMessage(level: LogLevel, message: string): string {
    let formattedMessage = message;
    
    if (this.options.namespace) {
      formattedMessage = `[${this.options.namespace}] ${formattedMessage}`;
    }
    
    if (this.options.logTimestamps) {
      const timestamp = new Date().toISOString();
      formattedMessage = `[${timestamp}] ${formattedMessage}`;
    }
    
    return formattedMessage;
  }

  /**
   * Export the logger's current state and settings
   * Useful for debugging and diagnostics
   */
  exportState(): { 
    options: LoggerOptions; 
    statistics: { 
      totalLogsThisMinute: number; 
      recentLogCount: number; 
      minuteStarted: string; 
      timeRemaining: number 
    }; 
    recentLogs: { 
      message: string; 
      count: number; 
      lastSeen: string 
    }[] 
  } {
    const recentLogsArray = Array.from(this.recentLogs.entries()).map(([key, data]) => ({
      message: key,
      count: data.count,
      lastSeen: new Date(data.timestamp).toISOString()
    }));
    
    return {
      options: this.options,
      statistics: {
        totalLogsThisMinute: this.logCounter,
        recentLogCount: this.recentLogs.size,
        minuteStarted: new Date(this.lastResetTime).toISOString(),
        timeRemaining: Math.max(0, 60000 - (Date.now() - this.lastResetTime))
      },
      recentLogs: recentLogsArray.slice(0, 20) // Only include the first 20 for brevity
    };
  }
}

// Default logger instance
export const logger = new Logger();

// Create pre-configured loggers for specific utilities
export const performanceLogger = logger.getNamespacedLogger('Performance');
export const diagnosticLogger = logger.getNamespacedLogger('Diagnostic');
export const webglLogger = logger.getNamespacedLogger('WebGL');
export const browserLogger = logger.getNamespacedLogger('Browser');
export const imageLogger = logger.getNamespacedLogger('Image');

// Flag for development tools configuration
// const devToolsConfigured = false;

/**
 * Configure window access for the logger in development environments
 * @param enable Whether to expose the logger on the window object
 */
export function configureLoggerDevTools(enable = true): void {
  // Type check for window needed because of global.d.ts
  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    if (enable) {
      // Use the globally defined Window interface
      window.logger = logger;
      
      window.loggerStats = () => {
        return {
          performanceLogger: performanceLogger.exportState(),
          diagnosticLogger: diagnosticLogger.exportState(),
          webglLogger: webglLogger.exportState(),
          browserLogger: browserLogger.exportState(),
          mainLogger: logger.exportState()
        };
      };
    } else {
      // Check before deleting
      if (window.logger) {
        delete window.logger;
      }
      if (window.loggerStats) {
        delete window.loggerStats;
      }
    }
  }
}

// Only automatically expose logger in development by default
if (process.env.REACT_APP_EXPOSE_LOGGER !== 'false' && process.env.NODE_ENV !== 'production') {
  configureLoggerDevTools(true);
} 
